import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCaseById, fetchRequiredFiles } from '../../api/cases';
import { Case, RequiredFile } from '../../types';
import { CheckCircleFill, ExclamationCircleFill, Clock } from 'react-bootstrap-icons';
import FileUploadZone from '../../components/FileUploadZone';
import CaseFiles from '../cases/CaseFiles';
import 'bootstrap-icons/font/bootstrap-icons.css';

const AttorneyPortal: React.FC = () => {
  const { case_id } = useParams<{ case_id: string }>();
  const auth = useAuth();
  
  const [caseDetails, setCaseDetails] = useState<Case | null>(null);
  const [requirements, setRequirements] = useState<RequiredFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const caseFilesRef = useRef<{ loadFiles: () => void }>(null);

  const loadData = useCallback(async () => {
    if (!auth.user?.id_token || !case_id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load case details
      const caseResponse = await fetchCaseById(case_id, auth.user.id_token);
      setCaseDetails(caseResponse.data);
      
      // Load requirements
      const reqResponse = await fetchRequiredFiles(case_id, auth.user.id_token);
      setRequirements(reqResponse.data.requirements || []);
      
    } catch (err: any) {
      console.error('Error loading data:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to this case.');
      } else {
        setError('Failed to load case data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUploadComplete = async () => {
    // Refresh files using the CaseFiles component ref (no page reload)
    if (caseFilesRef.current) {
      caseFilesRef.current.loadFiles();
    }
    
    // Reload requirements without triggering loading state (no flash)
    if (!auth.user?.id_token || !case_id) return;
    try {
      const reqResponse = await fetchRequiredFiles(case_id, auth.user.id_token);
      setRequirements(reqResponse.data.requirements || []);
    } catch (err) {
      console.error('Error refreshing requirements:', err);
    }
  };

  const handleFileDeleted = async () => {
    // Refresh files and requirements without full page reload
    if (caseFilesRef.current) {
      caseFilesRef.current.loadFiles();
    }
    
    if (!auth.user?.id_token || !case_id) return;
    try {
      const reqResponse = await fetchRequiredFiles(case_id, auth.user.id_token);
      setRequirements(reqResponse.data.requirements || []);
    } catch (err) {
      console.error('Error refreshing requirements:', err);
    }
  };

  // Helper to get requirement completion stats
  const getRequirementStats = (req: RequiredFile) => {
    // For bank statements, use required_months
    if (req.file_type === 'bank_statement' && req.required_months && req.required_months.length > 0) {
      const satisfiedMonths = req.required_months.filter(m => m.status === 'satisfied');
      return {
        matchedCount: satisfiedMonths.length,
        needed: req.required_months.length,
        isBankStatement: true,
        satisfiedMonths
      };
    }
    
    // For other file types, use matched_files
    const matchedFiles = req.matched_files || [];
    const uniqueFileIds = new Set(matchedFiles.map(f => f.file_id));
    return {
      matchedCount: uniqueFileIds.size,
      needed: req.required_count || 1,
      isBankStatement: false,
      satisfiedMonths: []
    };
  };

  // Format file type for display
  const formatFileType = (fileType: string) => {
    return fileType
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading case...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Access Error</Alert.Heading>
          <p>{error}</p>
          <Link to="/attorney" className="btn btn-outline-danger">
            Back to My Cases
          </Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Case Header */}
      <div className="mb-4">
        <Link to="/attorney" className="text-decoration-none text-muted small">
          <i className="bi bi-arrow-left me-1"></i> Back to My Cases
        </Link>
        <div className="d-flex justify-content-between align-items-start mt-2">
          <div>
            <h2 className="mb-1">{caseDetails?.case_title || 'Case'}</h2>
            <div className="text-muted">
              <span className="me-3">
                <i className="bi bi-hash me-1"></i>
                {caseDetails?.case_number}
              </span>
              <span className="me-3">
                <i className="bi bi-person me-1"></i>
                {caseDetails?.client}
              </span>
              <Badge bg={caseDetails?.status === 'active' ? 'success' : 'secondary'}>
                {caseDetails?.status}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Top Row - Upload Zone and Requirements */}
      <Row className="mb-4">
        {/* Left - Upload Zone */}
        <Col lg={7}>
          <FileUploadZone
            case_id={case_id!}
            id_token={auth.user?.id_token}
            onUploadComplete={handleUploadComplete}
            compact={true}
          />
        </Col>

        {/* Right - Requirements */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
              <strong className="small">
                <i className="bi bi-list-check me-2"></i>
                Document Requirements
              </strong>
              <small className="text-muted">
                {requirements.filter(r => {
                  const stats = getRequirementStats(r);
                  return stats.matchedCount >= stats.needed;
                }).length}/{requirements.length} complete
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              {requirements.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-clipboard-check"></i>
                  <p className="mb-0 mt-1 small">No requirements set</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">Document Type</th>
                        <th style={{ width: '100px' }} className="text-center">Progress</th>
                        <th style={{ width: '80px' }} className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requirements.map((req) => {
                        const stats = getRequirementStats(req);
                        const isComplete = stats.matchedCount >= stats.needed;
                        const progressPercent = Math.min(100, (stats.matchedCount / stats.needed) * 100);
                        
                        return (
                          <tr key={req.requirement_id}>
                            <td className="ps-3">
                              <div className="fw-medium small">{formatFileType(req.file_type)}</div>
                              {req.file_type === 'bank_statement' && req.date_range && (
                                <small className="text-muted">
                                  {req.date_range.from} to {req.date_range.to}
                                </small>
                              )}
                              {req.description && !req.date_range && (
                                <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>
                                  {req.description}
                                </small>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <small className={isComplete ? 'text-success fw-medium' : 'text-muted'}>
                                  {stats.matchedCount}/{stats.needed}
                                </small>
                                <ProgressBar 
                                  now={progressPercent} 
                                  variant={isComplete ? 'success' : progressPercent > 0 ? 'warning' : 'secondary'}
                                  style={{ width: '40px', height: '6px' }}
                                />
                              </div>
                            </td>
                            <td className="text-center align-middle">
                              {isComplete ? (
                                <CheckCircleFill className="text-success" size={16} />
                              ) : stats.matchedCount > 0 ? (
                                <Clock className="text-warning" size={16} />
                              ) : (
                                <ExclamationCircleFill className="text-danger" size={16} />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Full Width Row - Files Table */}
      <Row>
        <Col xs={12}>
          <CaseFiles
            ref={caseFilesRef}
            case_id={case_id!}
            onFileDeleted={handleFileDeleted}
            requirements={requirements}
            onFileMatched={handleFileDeleted}
            canDeleteFile={(file) => file.uploaded_by === auth.user?.profile?.email}
            canBulkDelete={false}
            hideManualMatch={true}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default AttorneyPortal;

